"use client";

import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { useT } from "@/Hooks";
import { FaqSectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement, ReactNode } from "react";
import HomeEightFaqSectionGenerated, {
  HomeEightFaqSectionGeneratedProps,
} from "../Sections/_generated/HomeEightFaqSection.generated";
import HomeEighteenFaqSectionGenerated, {
  HomeEighteenFaqSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenFaqSection.generated";
import HomeFifteenFaqSectionGenerated, {
  HomeFifteenFaqSectionGeneratedProps,
} from "../Sections/_generated/HomeFifteenFaqSection.generated";
import HomeFourFaqSectionGenerated, {
  HomeFourFaqSectionGeneratedProps,
} from "../Sections/_generated/HomeFourFaqSection.generated";
import HomeFourteenFaqSectionGenerated, {
  HomeFourteenFaqSectionGeneratedProps,
} from "../Sections/_generated/HomeFourteenFaqSection.generated";
import HomeOneFaqSectionGenerated, {
  HomeOneFaqSectionGeneratedProps,
} from "../Sections/_generated/HomeOneFaqSection.generated";
import HomeSeventeenFaqSectionGenerated, {
  HomeSeventeenFaqSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenFaqSection.generated";
import HomeSixFaqSectionGenerated, {
  HomeSixFaqSectionGeneratedProps,
} from "../Sections/_generated/HomeSixFaqSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type FaqTranslate = ReturnType<typeof useT>;
type GeneratedFaqRenderer = (
  props: FaqSectionProps,
  t: FaqTranslate,
) => ReactElement;
type GeneratedFaqProps = Record<string, unknown>;
type GeneratedFaqItem = Record<string, unknown>;

const FaqAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateFaqAppearance;

const titleKeyMatchers = [/H20H2Text$/, /H30H3Text$/];

const titleDecorationKeyMatchers = [
  /H20Span0SpanText$/,
  /H20Span1SpanText$/,
  /H20H2Text2$/,
];

const subtitleKeyMatchers = [/DivText$/];

const descriptionKeyMatchers = [/P0PText$/];

const imageKeyMatchers = [/ImageWithBasePath0ImageSrc$/];

function buildGeneratedFaqProps<T extends GeneratedFaqProps>(
  sectionKey: string,
  overrides?: Partial<T>,
): T {
  return buildGeneratedSectionProps<T>(sectionKey, overrides);
}

function findFirstMatchingKey(
  target: GeneratedFaqProps | GeneratedFaqItem,
  matchers: RegExp[],
): string | undefined {
  return Object.keys(target).find((key) =>
    matchers.some((matcher) => matcher.test(key)),
  );
}

function setFirstMatchingValue(
  target: GeneratedFaqProps | GeneratedFaqItem,
  matchers: RegExp[],
  value: unknown,
): void {
  if (value === null || value === undefined || value === "") {
    return;
  }

  const key = findFirstMatchingKey(target, matchers);
  if (!key) {
    return;
  }

  target[key] = value;
}

function setAllMatchingValues(
  target: GeneratedFaqProps | GeneratedFaqItem,
  matchers: RegExp[],
  value: unknown,
): void {
  if (value === null || value === undefined) {
    return;
  }

  for (const key of Object.keys(target)) {
    if (matchers.some((matcher) => matcher.test(key))) {
      target[key] = value;
    }
  }
}

function getFaqImageUrl(props: FaqSectionProps): string | undefined {
  const url = props.setting.Image?.Url;
  return typeof url === "string" && url.length > 0 ? url : undefined;
}

function getFaqQuestionKey(item: GeneratedFaqItem): string | undefined {
  if ("link0Text" in item) {
    return "link0Text";
  }

  if ("a0Text" in item) {
    return "a0Text";
  }

  return undefined;
}

function isFaqCollectionItem(item: GeneratedFaqItem): boolean {
  return !!getFaqQuestionKey(item) && "p0Text" in item;
}

function applyFaqCollection(
  defaults: GeneratedFaqItem[],
  items: FaqSectionProps["dataSource"]["Items"],
): GeneratedFaqItem[] {
  if (items.length === 0) {
    return [];
  }

  return items.map((faq, index) => {
    const baseItem = defaults[index % defaults.length] ?? {};
    const questionKey = getFaqQuestionKey(baseItem);
    const nextItem: GeneratedFaqItem = {
      ...baseItem,
      key: faq.Id,
      p0Text: faq.Answer,
    };

    if (questionKey) {
      nextItem[questionKey] = faq.Question;
    }

    return nextItem;
  });
}

function applyFaqOverrides<T extends GeneratedFaqProps>(
  generatedProps: T,
  props: FaqSectionProps,
): T {
  const nextProps: GeneratedFaqProps = {
    ...generatedProps,
  };
  const appearanceKey = props.setting.Appearance?.Key;

  setFirstMatchingValue(nextProps, titleKeyMatchers, props.setting.Title);
  if (props.setting.Title) {
    setAllMatchingValues(nextProps, titleDecorationKeyMatchers, "");
  }
  setFirstMatchingValue(
    nextProps,
    subtitleKeyMatchers,
    props.setting.Subtitle,
  );
  setFirstMatchingValue(
    nextProps,
    descriptionKeyMatchers,
    props.setting.Description,
  );

  const imageUrl = getFaqImageUrl(props);
  if (
    imageUrl &&
    appearanceKey !== FaqAppearance.Home1.key &&
    appearanceKey !== FaqAppearance.Home4.key &&
    appearanceKey !== FaqAppearance.Home14.key &&
    appearanceKey !== FaqAppearance.Home15.key &&
    appearanceKey !== FaqAppearance.Home17.key
  ) {
    setFirstMatchingValue(nextProps, imageKeyMatchers, imageUrl);
  }

  const faqCollectionKeys = Object.keys(nextProps).filter((key) => {
    const value = nextProps[key];
    return (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "object" &&
      value[0] !== null &&
      isFaqCollectionItem(value[0] as GeneratedFaqItem)
    );
  });

  let offset = 0;
  faqCollectionKeys.forEach((key, index) => {
    const defaults = nextProps[key] as GeneratedFaqItem[];
    const isLastCollection = index === faqCollectionKeys.length - 1;
    const nextItems = isLastCollection
      ? props.dataSource.Items.slice(offset)
      : props.dataSource.Items.slice(offset, offset + defaults.length);

    nextProps[key] = applyFaqCollection(defaults, nextItems);
    offset += nextItems.length;
  });

  return nextProps as T;
}

function renderPlainTitle(
  title: string | undefined,
  className: string,
  defaultNode: ReactNode,
): ReactNode {
  if (!title) {
    return defaultNode;
  }

  if (!className) {
    return <h2>{title}</h2>;
  }

  return <h2 className={className}>{title}</h2>;
}

const generatedFaqRenderers: Record<string, GeneratedFaqRenderer> = {
  [FaqAppearance.Home1.key]: (props) => (
    <HomeOneFaqSectionGenerated
      {...applyFaqOverrides(
        buildGeneratedFaqProps<HomeOneFaqSectionGeneratedProps>(
          "HomeOneFaqSection",
        ),
        props,
      )}
      sectionDiv0Div0Div0DivText={
        <Html value={props.setting.Description} />
      }
      sectionDiv0Div0H20H2Renderer={() => (
        <h2 className="section-title">
          <Html as="span" value={props.setting.Title} />
        </h2>
      )}
    />
  ),
  [FaqAppearance.Home4.key]: (props) => (
    <HomeFourFaqSectionGenerated
      {...applyFaqOverrides(
        buildGeneratedFaqProps<HomeFourFaqSectionGeneratedProps>(
          "HomeFourFaqSection",
        ),
        props,
      )}
      sectionDiv0Div0Div0Div0H20H2Renderer={(defaultNode) =>
        renderPlainTitle(props.setting.Title, "section-title", defaultNode)
      }
    />
  ),
  [FaqAppearance.Home6.key]: (props, t) => (
    <HomeSixFaqSectionGenerated
      {...applyFaqOverrides(
        buildGeneratedFaqProps<HomeSixFaqSectionGeneratedProps>(
          "HomeSixFaqSection",
          {
            sectionDiv0Div1Div0Div1Div0Div0Div1H40Link0LinkHref: props.pageData
              .CommonData.Email
              ? `mailto:${props.pageData.CommonData.Email}`
              : "",
            sectionDiv0Div1Div0Div1Div0Div0Div1H40Link0LinkText:
              props.pageData.CommonData.Email ?? "",
            sectionDiv0Div1Div0Div1Div0Div0Div1P0PText: t(
              "landing.footer.contactUs",
              undefined,
              "Contact us",
            ),
            sectionDiv0Div1Div0Div1Div0Div0Div1H40Link0LinkVisible:
              !!props.pageData.CommonData.Email,
          },
        ),
        props,
      )}
      sectionDiv0Div0H20H2Renderer={(defaultNode) =>
        renderPlainTitle(props.setting.Title, "section-title", defaultNode)
      }
    />
  ),
  [FaqAppearance.Home8.key]: (props) => (
    <HomeEightFaqSectionGenerated
      {...applyFaqOverrides(
        buildGeneratedFaqProps<HomeEightFaqSectionGeneratedProps>(
          "HomeEightFaqSection",
        ),
        props,
      )}
      sectionDiv0Div0Div1Div0H20H2Renderer={(defaultNode) =>
        renderPlainTitle(props.setting.Title, "section-title", defaultNode)
      }
    />
  ),
  [FaqAppearance.Home14.key]: (props) => (
    <HomeFourteenFaqSectionGenerated
      {...applyFaqOverrides(
        buildGeneratedFaqProps<HomeFourteenFaqSectionGeneratedProps>(
          "HomeFourteenFaqSection",
        ),
        props,
      )}
      sectionDiv0Div0Div0Div0H20H2Renderer={(defaultNode) =>
        renderPlainTitle(props.setting.Title, "section-title", defaultNode)
      }
    />
  ),
  [FaqAppearance.Home15.key]: (props) => (
    <HomeFifteenFaqSectionGenerated
      {...applyFaqOverrides(
        buildGeneratedFaqProps<HomeFifteenFaqSectionGeneratedProps>(
          "HomeFifteenFaqSection",
        ),
        props,
      )}
      sectionDiv0Div0Div1Div0H20H2Renderer={(defaultNode) =>
        renderPlainTitle(props.setting.Title, "section-title", defaultNode)
      }
    />
  ),
  [FaqAppearance.Home17.key]: (props) => (
    <HomeSeventeenFaqSectionGenerated
      {...applyFaqOverrides(
        buildGeneratedFaqProps<HomeSeventeenFaqSectionGeneratedProps>(
          "HomeSeventeenFaqSection",
        ),
        props,
      )}
      sectionDiv0Div0Div0Div0H20H2Renderer={(defaultNode) =>
        renderPlainTitle(props.setting.Title, "", defaultNode)
      }
    />
  ),
  [FaqAppearance.Home18.key]: (props) => (
    <HomeEighteenFaqSectionGenerated
      {...applyFaqOverrides(
        buildGeneratedFaqProps<HomeEighteenFaqSectionGeneratedProps>(
          "HomeEighteenFaqSection",
        ),
        props,
      )}
      sectionDiv0Div0Div0DivText={<Html value={props.setting.Description} />}
      sectionDiv0Div0H20H2Renderer={(defaultNode) =>
        renderPlainTitle(props.setting.Title, "section-title", defaultNode)
      }
    />
  ),
};

const GeneratedFaqSection: FC<FaqSectionProps> = (props) => {
  const t = useT();

  return renderGeneratedSection(
    props,
    Object.fromEntries(
      Object.entries(generatedFaqRenderers).map(([appearanceKey, renderer]) => [
        appearanceKey,
        (sectionProps: FaqSectionProps) => renderer(sectionProps, t),
      ]),
    ),
    FaqAppearance.Home1.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedFaqSection;
